/**
 * The studio's JSON editor: name, site and the config document, the
 * problems at their paths as the text changes (checked by the view with the
 * engine's schema, `lib/studio/config.ts`), and save — disabled while the
 * document is invalid, unchanged, or the user may not configure studios.
 * The text is the view's state (a conflict reload replaces it).
 */
import type { ConfigProblem } from '../../lib/studio/config.js';
import { Button } from '../ui/button.js';
import { Field } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { Textarea } from '../ui/textarea.js';
import { ConfigProblemList } from './ConfigProblemList.js';

export type StudioConfigEditorProps = {
  readonly name: string;
  readonly siteId: string;
  readonly text: string;
  readonly problems: readonly ConfigProblem[];
  readonly problemSource: 'editor' | 'server';
  /** The version the edit is made from. */
  readonly version: number;
  readonly dirty: boolean;
  readonly canSave: boolean;
  readonly pending: boolean;
  readonly onName: (name: string) => void;
  readonly onSite: (siteId: string) => void;
  readonly onText: (text: string) => void;
  readonly onSave: () => void;
  readonly onRevert: () => void;
};

export function StudioConfigEditor(props: StudioConfigEditorProps) {
  const readOnly = !props.canSave;
  const saveable = props.canSave && props.dirty && props.problems.length === 0 && props.name.trim() !== '' && !props.pending;
  return (
    <div className="grid gap-4" data-testid="studio-config-editor">
      <div className="grid grid-cols-2 gap-3">
        <Field>
          <Label htmlFor="studio-edit-name">Name</Label>
          <Input id="studio-edit-name" value={props.name} onChange={(e) => props.onName(e.target.value)} readOnly={readOnly} />
        </Field>
        <Field>
          <Label htmlFor="studio-edit-site">Site</Label>
          <Input id="studio-edit-site" value={props.siteId} onChange={(e) => props.onSite(e.target.value)} readOnly={readOnly} placeholder="none" />
        </Field>
      </div>
      <Field>
        <Label htmlFor="studio-edit-config">Config (JSON)</Label>
        <Textarea
          id="studio-edit-config"
          className="min-h-96 font-mono text-xs"
          spellCheck={false}
          value={props.text}
          onChange={(e) => props.onText(e.target.value)}
          readOnly={readOnly}
          aria-invalid={props.problems.length > 0}
          data-testid="studio-config-text"
        />
      </Field>
      <ConfigProblemList problems={props.problems} source={props.problemSource} />
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">
          Editing version {props.version}
          {readOnly ? ' — read only (configuring studios needs device.configure)' : ''}
        </span>
        <Button variant="outline" className="ml-auto" onClick={props.onRevert} disabled={!props.dirty || props.pending}>
          Revert
        </Button>
        <Button onClick={props.onSave} disabled={!saveable} data-testid="save-studio">
          {props.pending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
